import { fillGradient } from './gradient';
import { drawPlainPoint, drawPlainStroke } from './plain';
import { createOpacityStroke } from './pressureOpacity';
import { getStrokeBounds, translatePoints } from './points';

export function drawPressureStroke(stroke, context, brushSettings) {
  const {
    points,
    color,
    lineWidth,
    gradientColor,
  } = stroke;

  // Вычисляем границы штриха
  const bounds = getStrokeBounds(points, lineWidth);
  if (!bounds) return;

  // Создаем временный канвас минимального размера
  const strokeMaskCanvas = document.createElement('canvas');
  strokeMaskCanvas.width = bounds.width;
  strokeMaskCanvas.height = bounds.height;
  const strokeMaskCtx = strokeMaskCanvas.getContext('2d');

  // Переносим координаты точек, чтобы они были относительно верхнего левого угла временного канваса
  const offsetX = -bounds.x;
  const offsetY = -bounds.y;
  const translatedPoints = translatePoints(points, offsetX, offsetY);

  // Рисуем штрих на временном канвасе, используя перенесенные точки
  createPressureStroke({...stroke, color: 'black', points: translatedPoints}, strokeMaskCtx, brushSettings);
  fillGradient(strokeMaskCtx, translatedPoints, color, gradientColor, lineWidth);

  // Переносим результат на основной холст
  context.drawImage(
    strokeMaskCanvas,
    0, 0, bounds.width, bounds.height,
    bounds.x, bounds.y, bounds.width, bounds.height
  );
}



export function createPressureStroke (stroke, context, brushSettings) {

  const {
    lineWidth,
    increaseLineWidth = 0,
    points,
    isEffect,
  } = stroke;

  const {
    pressureOn,
    pressureOpacity,
    pressureAvailable,
    moreCache,
  } = brushSettings;

  const pressureCoef = 1;

  const turnedOn = pressureOn || pressureOpacity;

  if (!pressureAvailable || !turnedOn || !points[0].pressure || isEffect) {
    return drawPlainStroke(stroke, context, brushSettings);
  }
  if (brushSettings.pressureOpacity) {
    return createOpacityStroke(stroke, context, brushSettings);
  }

  if (points.length === 1) {
    return drawPoint(stroke, context, brushSettings);
  }

  context.lineCap = brushSettings.squareBrush ? 'square' : 'round';
  context.lineJoin = brushSettings.squareBrush ? 'bevel' : 'round';
  if (stroke.lineDash) { context.setLineDash(stroke.lineDash); }

  const smoothPoints = interpolatePoints(points, moreCache ? 0.3 : 1);
  const totalPoints = smoothPoints.length;

  context.strokeStyle = 'rgba(255, 255, 255, 1)';

  for (let i = 0; i < totalPoints - 1; i++) {

    const startPoint = smoothPoints[i];
    const endPoint = smoothPoints[i + 1];

    const segmentWidth = lineWidth * (startPoint.pressure + endPoint.pressure) / 2 * pressureCoef;

    if (segmentWidth === 0) { continue; }

    context.beginPath();
    context.moveTo(startPoint.x, startPoint.y);
    context.lineTo(endPoint.x, endPoint.y);
    context.lineWidth = segmentWidth + increaseLineWidth;
    context.stroke();

  }

  
}


export function drawPoint(stroke, bufferCtx, brushSettings) {

  const lineWidth = stroke.lineWidth * (stroke.points[0]?.pressure || 1)
  return drawPlainPoint({
    ...stroke,
    lineWidth,
  }, bufferCtx, brushSettings)

}


export function interpolatePoints(points, interpolationCoef = 1, edgePoints = 0) {
  if (points.length <= 2) return points;

  function distance(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  let interpolatedPoints = [];

  // Начинаем с первой точки
  interpolatedPoints.push(points[0]);

  const interpolateAll = edgePoints === 0;

  for (let i = 1; i < points.length; i++) {
    const prevPoint = points[i - 1];
    const currentPoint = points[i];

    // Вычисляем расстояние между предыдущей и текущей точкой
    const dist = distance(prevPoint, currentPoint);

    // Определяем, нужно ли интерполировать этот отрезок
    const shouldInterpolate = interpolateAll || (i <= edgePoints || i >= points.length - edgePoints);

    // Определяем количество интерполяций
    const numInterpolations = shouldInterpolate ? Math.min(20 * interpolationCoef, Math.round(interpolationCoef * dist)) : 0;

    // Вычисляем среднюю точку между предыдущей и текущей точкой
    const midPointPressure = (prevPoint.pressure + currentPoint.pressure) / 2;
    const midPoint = {
      x: (prevPoint.x + currentPoint.x) / 2,
      y: (prevPoint.y + currentPoint.y) / 2,
    };
    if (midPointPressure) {
      midPoint.pressure = midPointPressure;
    }

    const controlPoint = prevPoint;
    const startPoint = interpolatedPoints[interpolatedPoints.length - 1];
    const endPoint = midPoint;

    // Интерполируем точки по квадратичной кривой Безье
    for (let j = 1; j <= numInterpolations; j++) {
      const t = j / (numInterpolations + 1);

      const x =
        (1 - t) * (1 - t) * startPoint.x +
        2 * (1 - t) * t * controlPoint.x +
        t * t * endPoint.x;
      const y =
        (1 - t) * (1 - t) * startPoint.y +
        2 * (1 - t) * t * controlPoint.y +
        t * t * endPoint.y;
      const pressure =
        (1 - t) * (1 - t) * startPoint.pressure +
        2 * (1 - t) * t * controlPoint.pressure +
        t * t * endPoint.pressure;

      const point = { x, y };
      if (pressure) { point.pressure = pressure; }
      interpolatedPoints.push(point);
    }

    // Добавляем конечную точку сегмента
    interpolatedPoints.push(endPoint);
  }

  return interpolatedPoints;
}
